/**
 * BOGO Bundle Builder
 * State machine managing product selection, sticky bar, and cart integration.
 *
 * States: 0 items → 1 → 2 (checkout ready) → 3 → 4 (bonus checkout ready)
 * Each item tracks: productId, variantId, title, price, compareAtPrice, imageUrl, handle
 */

(function() {
  'use strict';

  const BOGO = {
    items: [],
    maxItems: 4,
    minCheckout: 2,
    bar: null,
    grid: null,
    overlay: null,
    quickView: null,
    isLoading: false,
    qvSwiper: null,

    init() {
      this.bar = document.querySelector('.bogo-bar');
      this.grid = document.querySelector('.bogo-grid');
      this.overlay = document.querySelector('.bogo-quick-view-overlay');
      this.quickView = document.querySelector('.bogo-quick-view');

      if (!this.bar || !this.grid) return;

      this.restoreState();
      this.bindEvents();
      this.markUnavailableVariants();
      this.render();

      // Show bar after a brief delay
      requestAnimationFrame(() => {
        this.bar.classList.add('bogo-bar--visible');
      });
    },

    // --- Sold-Out Variant Marking ---

    markUnavailableVariants() {
      const cards = this.grid.querySelectorAll('[data-bogo-product-id]');
      cards.forEach(card => {
        const productCard = card.querySelector('product-card');
        if (!productCard) return;

        const refEl = productCard.querySelector('[data-product-reference]');
        if (!refEl) return;

        const options = JSON.parse(refEl.dataset.options || '[]');
        const variantEls = productCard.querySelectorAll('[data-variant-reference]');

        // Build a map of option values to availability
        // For each option position, track which values have at least one available variant
        const optionAvailability = {};
        options.forEach((optName, idx) => {
          optionAvailability[optName] = {};
        });

        variantEls.forEach(vEl => {
          const vOptions = JSON.parse(vEl.dataset.options || '[]');
          const available = vEl.dataset.available === 'true';
          vOptions.forEach((val, idx) => {
            const optName = options[idx];
            if (optName) {
              if (!optionAvailability[optName][val]) {
                optionAvailability[optName][val] = false;
              }
              if (available) {
                optionAvailability[optName][val] = true;
              }
            }
          });
        });

        // Now mark selector buttons that have NO available variants
        const selectors = productCard.querySelectorAll('product-selector');
        selectors.forEach(selector => {
          const optName = selector.dataset.option;
          const buttons = selector.querySelectorAll('.selector__button');
          buttons.forEach(btn => {
            const value = btn.dataset.value;
            if (optionAvailability[optName] && optionAvailability[optName][value] === false) {
              btn.classList.add('bogo-unavailable');
            }
          });
        });

        // Also update the BOGO select button if the entire product is sold out
        const selectBtn = card.querySelector('.bogo-select-btn');
        if (selectBtn) {
          const anyAvailable = Array.from(variantEls).some(v => v.dataset.available === 'true');
          if (!anyAvailable && variantEls.length > 0) {
            selectBtn.disabled = true;
            selectBtn.textContent = 'Sold Out';
            selectBtn.dataset.bogoSoldOut = 'true';
          }
        }
      });
    },

    // --- State Management ---

    addItem(item) {
      if (this.items.length >= this.maxItems) return false;
      // Prevent duplicate product (same product, any variant)
      const existingIndex = this.items.findIndex(i => i.productId === item.productId);
      if (existingIndex !== -1) {
        // Update variant instead
        this.items[existingIndex] = item;
      } else {
        this.items.push(item);
      }
      this.saveState();
      this.render();
      return true;
    },

    removeItem(productId) {
      this.items = this.items.filter(i => i.productId !== productId);
      this.saveState();
      this.render();
    },

    updateItemVariant(productId, variantId, price, compareAtPrice, imageUrl) {
      const item = this.items.find(i => i.productId === productId);
      if (item) {
        item.variantId = variantId;
        item.price = price;
        item.compareAtPrice = compareAtPrice;
        if (imageUrl) item.imageUrl = imageUrl;
        this.saveState();
        this.render();
      }
    },

    isSelected(productId) {
      return this.items.some(i => i.productId === productId);
    },

    canAddMore() {
      return this.items.length < this.maxItems;
    },

    canCheckout() {
      return this.items.length >= this.minCheckout;
    },

    saveState() {
      try {
        sessionStorage.setItem('bogo_items', JSON.stringify(this.items));
      } catch(e) { /* silent */ }
    },

    restoreState() {
      try {
        const saved = sessionStorage.getItem('bogo_items');
        if (saved) {
          this.items = JSON.parse(saved);
          // Validate items still exist in grid
          this.items = this.items.filter(item => {
            return this.grid.querySelector(`[data-bogo-product-id="${item.productId}"]`);
          });
        }
      } catch(e) {
        this.items = [];
      }
    },

    // --- Rendering ---

    render() {
      this.renderBar();
      this.renderGrid();
    },

    renderBar() {
      const count = this.items.length;
      const slots = this.bar.querySelectorAll('.bogo-bar__slot');
      const message = this.bar.querySelector('.bogo-bar__message-text');
      const subMessage = this.bar.querySelector('.bogo-bar__message-sub');
      const ctaBtn = this.bar.querySelector('.bogo-bar__cta-btn');
      const ctaText = ctaBtn.querySelector('.bogo-bar__cta-text');
      const bonusSection = this.bar.querySelector('.bogo-bar__bonus');

      // Update slots
      slots.forEach((slot, i) => {
        const item = this.items[i];
        const imgEl = slot.querySelector('img');
        const removeBtn = slot.querySelector('.bogo-bar__slot-remove');

        if (item) {
          slot.classList.add('bogo-bar__slot--filled');
          slot.classList.remove('bogo-bar__slot--next');
          if (imgEl) {
            imgEl.src = item.imageUrl;
            imgEl.alt = item.title;
            imgEl.style.display = 'block';
          }
          if (removeBtn) {
            removeBtn.style.display = 'flex';
            removeBtn.dataset.bogoRemoveProductId = item.productId;
          }
        } else {
          slot.classList.remove('bogo-bar__slot--filled');
          slot.classList.remove('bogo-bar__slot--next');
          if (imgEl) imgEl.style.display = 'none';
          if (removeBtn) removeBtn.style.display = 'none';

          // Highlight the next slot to fill
          if (i === count) {
            slot.classList.add('bogo-bar__slot--next');
          }
        }
      });

      // Show/hide bonus pair
      if (bonusSection) {
        if (count >= 2) {
          bonusSection.classList.add('bogo-bar__bonus--active');
        } else {
          bonusSection.classList.remove('bogo-bar__bonus--active');
        }
      }

      // Update message
      if (count === 0) {
        message.textContent = 'Select your first piece';
        subMessage.textContent = 'Pick 2 items for Buy One Get One';
      } else if (count === 1) {
        message.textContent = 'Add one more piece!';
        subMessage.textContent = '1 more to complete your BOGO';
      } else if (count === 2) {
        message.textContent = 'BOGO ready!';
        subMessage.textContent = 'Add 2 more for a bonus deal, or checkout now';
      } else if (count === 3) {
        message.textContent = 'Add one more for the bonus!';
        subMessage.textContent = '1 more to complete the bonus pair';
      } else if (count === 4) {
        message.textContent = 'Double BOGO complete!';
        subMessage.textContent = 'You\'re getting the best deal';
      }

      // Update CTA
      if (count === 0) {
        ctaBtn.disabled = true;
        ctaText.textContent = 'Select 2 more';
      } else if (count === 1) {
        ctaBtn.disabled = true;
        ctaText.textContent = 'Select 1 more';
      } else if (count === 2) {
        ctaBtn.disabled = false;
        ctaText.textContent = 'Add Pair to Bag';
      } else if (count === 3) {
        ctaBtn.disabled = false;
        ctaText.textContent = 'Select 1 more';
      } else {
        ctaBtn.disabled = false;
        ctaText.textContent = 'Add All to Bag';
      }
    },

    renderGrid() {
      const cards = this.grid.querySelectorAll('[data-bogo-product-id]');
      cards.forEach(card => {
        const productId = card.dataset.bogoProductId;
        const isSelected = this.isSelected(productId);
        const selectBtn = card.querySelector('.bogo-select-btn');

        card.classList.toggle('bogo-selected', isSelected);

        if (selectBtn) {
          // Check if current selected variant is available
          const currentVariantAvailable = this.isCurrentVariantAvailable(card);

          if (selectBtn.dataset.bogoSoldOut === 'true') {
            // Entire product sold out - don't change
            return;
          } else if (isSelected) {
            selectBtn.textContent = 'Selected';
            selectBtn.classList.add('bogo-selected-btn');
            selectBtn.disabled = false;
          } else if (!currentVariantAvailable) {
            selectBtn.textContent = 'Sold Out';
            selectBtn.disabled = true;
            selectBtn.classList.remove('bogo-selected-btn');
          } else if (!this.canAddMore()) {
            selectBtn.textContent = 'Limit reached';
            selectBtn.disabled = true;
            selectBtn.classList.remove('bogo-selected-btn');
          } else {
            selectBtn.textContent = 'Select for BOGO';
            selectBtn.disabled = false;
            selectBtn.classList.remove('bogo-selected-btn');
          }
        }
      });
    },

    isCurrentVariantAvailable(card) {
      const productCard = card.querySelector('product-card');
      if (!productCard) return true;
      const refEl = productCard.querySelector('[data-product-reference]');
      if (!refEl) return true;
      const selectedId = refEl.dataset.selected;
      const variantEl = productCard.querySelector(`[data-variant-reference][data-id="${selectedId}"]`);
      if (!variantEl) return true;
      return variantEl.dataset.available === 'true';
    },

    // --- Event Binding ---

    bindEvents() {
      // Select/deselect from grid
      this.grid.addEventListener('click', (e) => {
        const selectBtn = e.target.closest('.bogo-select-btn');
        if (selectBtn) {
          e.preventDefault();
          e.stopPropagation();
          const card = selectBtn.closest('[data-bogo-product-id]');
          if (!card) return;
          this.handleSelectToggle(card);
          return;
        }

        // Quick view trigger - clicking product image/link
        const productLink = e.target.closest('.product-link, product-slider a');
        if (productLink) {
          e.preventDefault();
          e.stopPropagation();
          const card = productLink.closest('[data-bogo-product-id]');
          if (card) this.openQuickView(card);
          return;
        }
      });

      // Remove from bar slots
      this.bar.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('.bogo-bar__slot-remove');
        if (removeBtn) {
          const productId = removeBtn.dataset.bogoRemoveProductId;
          if (productId) this.removeItem(productId);
          return;
        }

        const ctaBtn = e.target.closest('.bogo-bar__cta-btn');
        if (ctaBtn && !ctaBtn.disabled) {
          this.addToCart();
          return;
        }
      });

      // Quick view overlay close
      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.closeQuickView());
      }
      if (this.quickView) {
        this.quickView.addEventListener('click', (e) => {
          if (e.target.closest('.bogo-qv__close')) {
            this.closeQuickView();
            return;
          }
          const qvSelectBtn = e.target.closest('.bogo-qv__select-btn');
          if (qvSelectBtn) {
            this.handleQuickViewSelect();
          }
        });
      }

      // Listen for variant changes within BOGO cards
      // The ProductCard web component dispatches 'optionvaluechange' internally,
      // which triggers optionChangeHandler → updates data-selected on the reference el.
      // We use a MutationObserver on data-selected to catch when the variant changes.
      this.grid.querySelectorAll('[data-product-reference]').forEach(refEl => {
        const observer = new MutationObserver(() => {
          const card = refEl.closest('[data-bogo-product-id]');
          if (!card) return;
          // Re-render grid to update sold-out state on select button
          this.renderGrid();
          // If this card is already selected, update the stored variant
          if (this.isSelected(card.dataset.bogoProductId)) {
            const variantData = this.getCardVariantData(card);
            if (variantData) {
              this.updateItemVariant(
                card.dataset.bogoProductId,
                variantData.variantId,
                variantData.price,
                variantData.compareAtPrice,
                variantData.imageUrl
              );
            }
          }
        });
        observer.observe(refEl, { attributes: true, attributeFilter: ['data-selected'] });
      });

      // ESC to close quick view
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.closeQuickView();
      });
    },

    handleSelectToggle(card) {
      const productId = card.dataset.bogoProductId;

      if (this.isSelected(productId)) {
        this.removeItem(productId);
      } else if (this.canAddMore()) {
        const variantData = this.getCardVariantData(card);
        if (variantData) {
          this.addItem({
            productId: productId,
            variantId: variantData.variantId,
            title: card.dataset.bogoProductTitle || '',
            price: variantData.price,
            compareAtPrice: variantData.compareAtPrice,
            imageUrl: variantData.imageUrl,
            handle: card.dataset.bogoProductHandle || ''
          });
        }
      }
    },

    getCardVariantData(card) {
      const refEl = card.querySelector('[data-product-reference]');
      if (!refEl) return null;

      let selectedVariantId = refEl.dataset.selected;
      // Fallback: if no variant selected, use the first variant reference
      if (!selectedVariantId) {
        const firstVariant = card.querySelector('[data-variant-reference][data-id]');
        if (firstVariant) selectedVariantId = firstVariant.dataset.id;
      }
      if (!selectedVariantId) return null;
      const variantRef = card.querySelector(`[data-variant-reference][data-id="${selectedVariantId}"]`);

      let price = card.dataset.bogoPrice || '0';
      let compareAtPrice = card.dataset.bogoComparePrice || '';
      let imageUrl = card.dataset.bogoImage || '';

      if (variantRef) {
        const priceMarkup = variantRef.querySelector('.variant-price-markup span');
        if (priceMarkup) price = priceMarkup.textContent.trim();
        const delMarkup = variantRef.querySelector('.variant-price-markup del');
        if (delMarkup) compareAtPrice = delMarkup.textContent.trim();
      }

      // Try to get the currently visible image
      const visibleImg = card.querySelector('.product-card__image-wrapper.is-selected img');
      if (visibleImg && visibleImg.src) {
        imageUrl = visibleImg.src;
      }

      return {
        variantId: selectedVariantId,
        price: price,
        compareAtPrice: compareAtPrice,
        imageUrl: imageUrl
      };
    },

    // --- Quick View ---

    // Store the mapping of slide index → variant IDs for the current quick view
    qvSlideVariantMap: [],
    // Store the source card reference for the open quick view
    qvSourceCard: null,

    /**
     * Read all card image wrappers (featured + model + detail) into a structured array.
     * Each entry: { src, alt, variantIds: string[], type: 'featured'|'model'|'detail' }
     */
    readCardImages(card, title) {
      const allImages = [];

      // Helper to extract image data from a wrapper element
      const extract = (wrapper, type) => {
        const img = wrapper.querySelector('img');
        if (!img) return null;
        let src = img.src || img.dataset.src || '';
        src = src.replace(/width=\d+/, 'width=800');
        const alt = img.alt || title;
        let variantIds = [];
        try {
          variantIds = JSON.parse(wrapper.dataset.variantIds || '[]');
        } catch(e) {
          variantIds = [];
        }
        return { src, alt, variantIds: variantIds.map(String), type };
      };

      // Collect featured images
      card.querySelectorAll('[data-featured-image]').forEach(wrapper => {
        const data = extract(wrapper, 'featured');
        if (data) allImages.push(data);
      });

      // Collect model images
      card.querySelectorAll('[data-model-image]').forEach(wrapper => {
        const data = extract(wrapper, 'model');
        if (data) allImages.push(data);
      });

      // Collect detail images (from hidden bogo-detail-images container)
      card.querySelectorAll('[data-detail-image]').forEach(wrapper => {
        const data = extract(wrapper, 'detail');
        if (data) allImages.push(data);
      });

      return allImages;
    },

    /**
     * Build an ordered slide array for a given variant ID.
     * Order: variant featured → variant model → variant detail → other featured → other model → other detail
     * Detail images with empty variantIds (product-level) show for all variants.
     * Deduplicates by image URL to prevent the same photo appearing twice.
     */
    buildSlidesForVariant(allImages, variantId) {
      const vid = String(variantId);

      // Separate variant-matching vs non-matching, by type
      const matchFeatured = [];
      const matchModel = [];
      const matchDetail = [];
      const otherFeatured = [];
      const otherModel = [];
      const otherDetail = [];

      allImages.forEach(img => {
        // Detail images with empty variantIds are product-level (show for all variants)
        const matches = img.variantIds.includes(vid) ||
          (img.type === 'detail' && img.variantIds.length === 0);

        if (matches && img.type === 'featured') matchFeatured.push(img);
        else if (matches && img.type === 'model') matchModel.push(img);
        else if (matches && img.type === 'detail') matchDetail.push(img);
        else if (img.type === 'featured') otherFeatured.push(img);
        else if (img.type === 'model') otherModel.push(img);
        else otherDetail.push(img);
      });

      // Variant images first (featured → model → detail), then the rest
      let slides = [
        ...matchFeatured, ...matchModel, ...matchDetail,
        ...otherFeatured, ...otherModel, ...otherDetail
      ];

      // Deduplicate by src URL (same image can appear in multiple wrappers)
      const seen = new Set();
      slides = slides.filter(s => {
        const key = s.src.split('?')[0];
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      return slides;
    },

    /**
     * Render the Swiper gallery with the given slides array and initialize it.
     */
    renderQvGallery(slides) {
      const qvImages = this.quickView.querySelector('.bogo-qv__images');
      if (!qvImages) return;

      // Destroy previous Swiper
      if (this.qvSwiper) {
        this.qvSwiper.destroy(true, true);
        this.qvSwiper = null;
      }

      // Build variant map for slideToVariant
      this.qvSlideVariantMap = slides.map(s => s.variantIds);

      const slidesHtml = slides.map(slide =>
        `<div class="swiper-slide"><img src="${slide.src}" alt="${(slide.alt || '').replace(/"/g, '&quot;')}"></div>`
      ).join('');

      qvImages.innerHTML =
        '<div class="swiper bogo-qv__swiper">' +
          '<div class="swiper-wrapper">' + slidesHtml + '</div>' +
          (slides.length > 1 ? '<div class="swiper-pagination bogo-qv__pagination"></div>' : '') +
        '</div>';

      if (slides.length > 1) {
        this.initQvSwiper(qvImages, 0, 0);
      }
    },

    openQuickView(card) {
      if (!this.quickView || !this.overlay) return;

      const productId = card.dataset.bogoProductId;
      const title = card.dataset.bogoProductTitle || '';
      const handle = card.dataset.bogoProductHandle || '';

      this.qvSourceCard = card;

      // Populate quick view
      const qvTitle = this.quickView.querySelector('.bogo-qv__title');
      const qvPrice = this.quickView.querySelector('.bogo-qv__price');
      const qvImages = this.quickView.querySelector('.bogo-qv__images');
      const qvSelectors = this.quickView.querySelector('.bogo-qv__selectors');
      const qvSelectBtn = this.quickView.querySelector('.bogo-qv__select-btn');
      const qvDescription = this.quickView.querySelector('.bogo-qv__description');

      if (qvTitle) qvTitle.textContent = title;

      // Clone price from card
      const cardPrice = card.querySelector('[data-list-price]');
      if (qvPrice && cardPrice) {
        qvPrice.innerHTML = cardPrice.innerHTML;
      }

      // Build image gallery from the card's featured + model image wrappers
      if (qvImages) {
        // Read all images from card DOM (featured + model, each with variant IDs)
        this.qvAllImages = this.readCardImages(card, title);

        // Get the currently selected variant ID
        const refEl = card.querySelector('[data-product-reference]');
        const currentVariantId = refEl ? refEl.dataset.selected : '';

        let slides = [];

        if (this.qvAllImages.length > 0) {
          // Build slides ordered for the current variant
          slides = this.buildSlidesForVariant(this.qvAllImages, currentVariantId);
        }

        // Fallback: use data-bogo-images if no card images found
        if (slides.length === 0) {
          let images = [];
          try {
            const raw = card.getAttribute('data-bogo-images');
            if (raw) images = JSON.parse(raw);
          } catch(e) {
            images = [];
          }
          if (!images || images.length === 0) {
            const fallbackImg = card.dataset.bogoImage;
            if (fallbackImg) {
              images = [{ src: fallbackImg, alt: title }];
            }
          }
          slides = images.map(img => ({ src: img.src, alt: img.alt || title, variantIds: [], type: 'featured' }));
          this.qvAllImages = slides;
        }

        this.renderQvGallery(slides);
      }

      // Clone selectors from card and wire up variant-image switching
      if (qvSelectors) {
        const cardSelectors = card.querySelector('.selectors');
        if (cardSelectors) {
          qvSelectors.innerHTML = '<div class="selectors">' + cardSelectors.innerHTML + '</div>';
          // Wire up click handlers on cloned selector buttons
          this.bindQvSelectorEvents(card, qvSelectors);
        } else {
          qvSelectors.innerHTML = '';
        }
      }

      // Description from data attribute
      if (qvDescription) {
        const desc = card.dataset.bogoProductDescription || '';
        qvDescription.innerHTML = desc;
        qvDescription.style.display = desc ? 'block' : 'none';
      }

      // Update select button
      this.quickView.dataset.bogoProductId = productId;
      if (qvSelectBtn) {
        if (this.isSelected(productId)) {
          qvSelectBtn.textContent = 'Remove from BOGO';
          qvSelectBtn.classList.add('bogo-qv__remove-btn');
          qvSelectBtn.disabled = false;
        } else if (this.canAddMore()) {
          qvSelectBtn.textContent = 'Select for BOGO';
          qvSelectBtn.classList.remove('bogo-qv__remove-btn');
          qvSelectBtn.disabled = false;
        } else {
          qvSelectBtn.textContent = 'BOGO slots full';
          qvSelectBtn.classList.remove('bogo-qv__remove-btn');
          qvSelectBtn.disabled = true;
        }
      }

      // Open
      this.overlay.classList.add('bogo-qv-open');
      this.quickView.classList.add('bogo-qv-open');
      document.body.style.overflow = 'hidden';
    },

    // All card images for the current quick view (persisted so swatch clicks can reorder)
    qvAllImages: [],

    /**
     * Bind click events on quick view selectors to:
     * 1. Update selected state visually in the QV
     * 2. Trigger the variant change on the source card (so data-selected updates)
     * 3. Rebuild the gallery ordered for the new variant (featured first, then model)
     */
    bindQvSelectorEvents(card, qvSelectors) {
      const refEl = card.querySelector('[data-product-reference]');
      if (!refEl) return;

      const options = JSON.parse(refEl.dataset.options || '[]');
      const variantEls = card.querySelectorAll('[data-variant-reference]');

      // Build variants array from card data
      const variants = [];
      variantEls.forEach(vEl => {
        variants.push({
          id: vEl.dataset.id,
          available: vEl.dataset.available === 'true',
          options: JSON.parse(vEl.dataset.options || '[]')
        });
      });

      // Track selected options (read current from card)
      const selectedOptions = {};
      const currentVariant = variants.find(v => v.id === refEl.dataset.selected);
      if (currentVariant) {
        options.forEach((optName, idx) => {
          selectedOptions[optName] = currentVariant.options[idx];
        });
      }

      // Listen for clicks on selector buttons in the quick view
      qvSelectors.addEventListener('click', (e) => {
        const btn = e.target.closest('.selector__button');
        if (!btn || btn.classList.contains('bogo-unavailable')) return;

        const selectorEl = btn.closest('[data-option]') || btn.closest('product-selector');
        if (!selectorEl) return;

        const optName = selectorEl.dataset.option || selectorEl.getAttribute('data-option');
        const value = btn.dataset.value;
        if (!optName || !value) return;

        // Update selected option
        selectedOptions[optName] = value;

        // Update visual selected state in QV selectors
        const siblingBtns = selectorEl.querySelectorAll('.selector__button');
        siblingBtns.forEach(b => b.classList.remove('selected-value'));
        btn.classList.add('selected-value');

        // Find matching variant
        const matchedVariant = variants.find(v => {
          return options.every((opt, idx) => v.options[idx] === selectedOptions[opt]);
        });

        if (matchedVariant) {
          // Update the source card's data-selected so BOGO state stays in sync
          refEl.setAttribute('data-selected', matchedVariant.id);

          // Update QV price
          const qvPrice = this.quickView.querySelector('.bogo-qv__price');
          const variantRef = card.querySelector(`[data-variant-reference][data-id="${matchedVariant.id}"]`);
          if (qvPrice && variantRef) {
            const priceMarkup = variantRef.querySelector('.variant-price-markup');
            if (priceMarkup) qvPrice.innerHTML = priceMarkup.innerHTML;
          }

          // Rebuild the gallery ordered for this variant
          // (variant's featured + model first, then the rest)
          const newSlides = this.buildSlidesForVariant(this.qvAllImages, matchedVariant.id);
          this.renderQvGallery(newSlides);
        }
      });
    },

    initQvSwiper(qvImages, attempt, initialSlide) {
      attempt = attempt || 0;
      initialSlide = initialSlide || 0;
      // Wait for Swiper global to be available (CDN defer)
      if (typeof Swiper === 'undefined') {
        if (attempt < 20) {
          setTimeout(() => this.initQvSwiper(qvImages, attempt + 1, initialSlide), 100);
        }
        return;
      }
      // Wait a frame for the modal to have layout dimensions
      setTimeout(() => {
        const swiperEl = qvImages.querySelector('.bogo-qv__swiper');
        if (!swiperEl) return;
        this.qvSwiper = new Swiper(swiperEl, {
          slidesPerView: 1,
          spaceBetween: 0,
          loop: false,
          initialSlide: initialSlide,
          pagination: {
            el: qvImages.querySelector('.bogo-qv__pagination'),
            clickable: true,
            type: 'bullets'
          }
        });
      }, 50);
    },

    closeQuickView() {
      if (this.overlay) this.overlay.classList.remove('bogo-qv-open');
      if (this.quickView) this.quickView.classList.remove('bogo-qv-open');
      document.body.style.overflow = '';
      // Clean up Swiper
      if (this.qvSwiper) {
        this.qvSwiper.destroy(true, true);
        this.qvSwiper = null;
      }
      this.qvSlideVariantMap = [];
      this.qvAllImages = [];
      this.qvSourceCard = null;
    },

    handleQuickViewSelect() {
      const productId = this.quickView.dataset.bogoProductId;
      if (!productId) return;

      if (this.isSelected(productId)) {
        this.removeItem(productId);
        this.closeQuickView();
      } else {
        const card = this.grid.querySelector(`[data-bogo-product-id="${productId}"]`);
        if (card) {
          this.handleSelectToggle(card);
          this.closeQuickView();
        }
      }
    },

    // --- Cart Integration ---

    async addToCart() {
      if (this.isLoading || !this.canCheckout()) return;
      this.isLoading = true;

      const ctaBtn = this.bar.querySelector('.bogo-bar__cta-btn');
      ctaBtn.classList.add('bogo-loading');
      ctaBtn.disabled = true;

      // Filter to only items with valid variant IDs
      const validItems = this.items.filter(item => item.variantId && !isNaN(parseInt(item.variantId)));
      if (validItems.length === 0) {
        console.error('BOGO: No valid variant IDs found', this.items);
        const ctaText = ctaBtn.querySelector('.bogo-bar__cta-text');
        if (ctaText) ctaText.textContent = 'Error - try again';
        this.isLoading = false;
        ctaBtn.classList.remove('bogo-loading');
        return;
      }

      const items = validItems.map(item => ({
        id: parseInt(item.variantId),
        quantity: 1
      }));

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: items })
        });

        if (!response.ok) {
          const errorBody = await response.json().catch(() => ({}));
          console.error('Shopify cart error:', errorBody);
          throw new Error(errorBody.description || 'Cart error');
        }

        const data = await response.json();

        // Publish cart update event for cart drawer/icon
        if (typeof publish === 'function') {
          publish('cart-update', {
            source: 'bogo-builder',
            cartData: data
          });
        }

        // Notify ReBuy mini-cart to refresh
        document.dispatchEvent(new CustomEvent('rebuy:cart:change'));

        // Clear BOGO selections
        this.items = [];
        this.saveState();
        this.render();

        // Update cart count in nav
        this.updateCartCount();

        // Show success then redirect to cart or open drawer
        const ctaText = ctaBtn.querySelector('.bogo-bar__cta-text');
        if (ctaText) ctaText.textContent = 'Added!';

        setTimeout(() => {
          // Try to open cart drawer
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          } else {
            // Open drawer via click
            const cartLink = document.querySelector('#cart-icon-bubble');
            if (cartLink) cartLink.click();
          }
        }, 400);

      } catch (err) {
        console.error('BOGO add to cart failed:', err);
        const ctaText = ctaBtn.querySelector('.bogo-bar__cta-text');
        if (ctaText) ctaText.textContent = 'Error - try again';
      } finally {
        this.isLoading = false;
        ctaBtn.classList.remove('bogo-loading');
        setTimeout(() => this.render(), 2000);
      }
    },

    async updateCartCount() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        const countBubbles = document.querySelectorAll('.cart-count-bubble span[aria-hidden]');
        countBubbles.forEach(el => {
          el.textContent = cart.item_count;
        });
      } catch(e) { /* silent */ }
    }
  };

  // Initialize when DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => BOGO.init());
  } else {
    BOGO.init();
  }

  // Expose for debugging
  window.BOGO = BOGO;
})();
